import { memo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { AiMessageFeedbackEnum, User } from 'graphql-common';
import Typography from '@lib/components/Typography/Typography';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import AssistantRow from './AssistantRow';
import parseContentBlocks, {
  AiContentBlockType,
  AiEntityRefBlock,
} from './utils/parseContentBlocks';
import formatMessageTime from './utils/formatMessageTime';
import { AiCitationRef } from './utils/citationPages';
import MessageMarkdown from './MessageMarkdown';
import SourceCard from './SourceCard';
import SuggestedFollowups from './SuggestedFollowups';
import MessageFeedback from './MessageFeedback';
import ToolStepsStrip, { AiToolStep } from './ToolStepsStrip';
import ThoughtsPanel from './ThoughtsPanel';
import UserMessageContent from './UserMessageContent';
import styles from './AiAssistant.module.scss';

type Props = {
  isUser?: boolean;
  isThinking?: boolean;
  // The assistant's extended-thinking (reasoning) text — the live buffer while
  // streaming, or the persisted message's `thinking` on reload. Rendered in a
  // collapsed "Thoughts" panel; absent/empty hides it.
  thinking?: string | null;
  // True while the assistant turn is still streaming — passed to MessageMarkdown
  // so inline document references defer their fetch until the message persists.
  isStreaming?: boolean;
  // Tool invocations of this turn (running + completed), folded into the top of
  // the bubble as a steps header (see ConversationPane grouping) so the whole
  // turn shares a single avatar instead of one per tool-activity row.
  toolSteps?: AiToolStep[];
  messageId?: string;
  content?: string | null;
  contentBlocks?: unknown;
  // Verified page citations of a persisted assistant message (empty while
  // streaming) — lets inline document chips offer "open at page N".
  citations?: AiCitationRef[];
  createdAt?: string | null;
  errorText?: string | null;
  suggestions?: string[];
  suggestionsDisabled?: boolean;
  onSuggestionSelect?: (suggestion: string) => void;
  showFeedback?: boolean;
  feedback?: AiMessageFeedbackEnum | null;
  feedbackDisabled?: boolean;
  onLike?: (messageId: string) => void;
  onDislike?: (messageId: string) => void;
  onRemoveFeedback?: (messageId: string) => void;
  user?: User;
};

function UserAvatar({ user }: { user?: User }) {
  const avatarUrl = _get(user, 'avatarAttached.urls.small') as
    string | undefined;
  return (
    <div className={styles.avatar}>
      {avatarUrl ? (
        <img src={avatarUrl} alt="" />
      ) : (
        <MaterialIcon icon="person" symbolsOutlined size="md-20" />
      )}
    </div>
  );
}

function MessageBubble(props: Props) {
  const {
    isUser,
    isThinking,
    thinking,
    isStreaming,
    toolSteps = [],
    messageId,
    content,
    contentBlocks,
    citations,
    createdAt,
    errorText,
    suggestions = [],
    suggestionsDisabled,
    onSuggestionSelect,
    showFeedback,
    feedback = null,
    feedbackDisabled,
    onLike,
    onDislike,
    onRemoveFeedback,
    user,
  } = props;
  const { t } = useTranslation();

  const entityRefs = parseContentBlocks(contentBlocks).filter(
    (block): block is AiEntityRefBlock =>
      block.type === AiContentBlockType.EntityRef,
  );
  const time = formatMessageTime(createdAt);

  if (isUser) {
    return (
      <div className={classNames(styles.messageRow, styles.messageRowUser)}>
        <div
          className={classNames(styles.messageBubble, styles.messageBubbleUser)}
        >
          <Typography
            variant="body"
            className={classNames(styles.messageText, styles.messageTextUser)}
          >
            <UserMessageContent content={content ?? ''} />
          </Typography>
          {!!time && (
            <Typography
              variant="caption"
              className={classNames(styles.messageTime, styles.messageTimeUser)}
            >
              {time}
            </Typography>
          )}
        </div>
        <UserAvatar user={user} />
      </div>
    );
  }

  return (
    <AssistantRow>
      {/* One bubble across the whole turn: the tool steps land inside it while
            the assistant is still thinking and stay put when the first token
            swaps the indicator out for markdown, instead of re-parenting from a
            bare strip into a bubble. The bubble carries the padding/background,
            so a thinking state with no steps still reads as the pill it was. */}
      <div
        className={classNames(
          styles.messageBubble,
          styles.messageBubbleAssistant,
        )}
      >
        <ToolStepsStrip
          toolSteps={toolSteps}
          showThinking={isThinking}
          // Divider whenever anything renders below the strip — the Thoughts
          // panel included. Reasoning shows above the answer and outside the
          // isThinking gate, so while the model is still thinking the strip is
          // followed by the panel and needs the same separator it gets once the
          // answer arrives (without this the divider only appeared post-thinking).
          withDivider={
            !!thinking || (!isThinking && (!!content || !!errorText || !!time))
          }
        />
        {/* Reasoning sits above the answer and outside the isThinking gate, so
            it shows while the model is still reasoning (no answer text yet) and
            stays put once the answer streams in. */}
        {!!thinking && (
          <ThoughtsPanel thinking={thinking} isStreaming={isStreaming} />
        )}
        {!isThinking && (
          <>
            {!!content && (
              <MessageMarkdown
                content={content}
                isStreaming={isStreaming}
                citations={citations}
              />
            )}
            {!!errorText && (
              <Typography variant="caption" className={styles.messageError}>
                {errorText}
              </Typography>
            )}
            {!!time && (
              <Typography variant="caption" className={styles.messageTime}>
                {time}
              </Typography>
            )}
          </>
        )}
      </div>
      {!!entityRefs.length && (
        <div className={styles.sourceSection}>
          <Typography variant="caption" className={styles.sourceSectionLabel}>
            {t('ai-source-label')}
          </Typography>
          {entityRefs.map((block) => (
            <SourceCard key={`${block.entityType}-${block.id}`} block={block} />
          ))}
        </div>
      )}
      {!!onSuggestionSelect && (
        <SuggestedFollowups
          suggestions={suggestions}
          disabled={suggestionsDisabled}
          onSelect={onSuggestionSelect}
        />
      )}
      {showFeedback &&
        !!messageId &&
        onLike &&
        onDislike &&
        onRemoveFeedback && (
          <MessageFeedback
            messageId={messageId}
            feedback={feedback}
            disabled={feedbackDisabled}
            onLike={onLike}
            onDislike={onDislike}
            onRemove={onRemoveFeedback}
          />
        )}
    </AssistantRow>
  );
}

// ConversationPane rebuilds its render groups (and the toolSteps/suggestions
// arrays each group carries) from scratch on every render, including the ones
// fired by a streamed token that belongs to a DIFFERENT, later turn — so
// without memoization every settled bubble in the transcript re-renders (and
// re-runs parseContentBlocks) on every delta of the turn currently streaming,
// which is what made long transcripts stutter while scrolling. Default
// React.memo's reference-based shallow compare can't see through that churn
// since toolSteps/suggestions are new array literals each time even when
// their contents are identical, so those two are compared by value here;
// everything else (content, thinking, timestamps, callbacks) is either a
// primitive or a stable reference and compares correctly by default.
function arePropsEqual(prev: Props, next: Props): boolean {
  const prevKeys = Object.keys(prev) as (keyof Props)[];
  const nextKeys = Object.keys(next) as (keyof Props)[];
  if (prevKeys.length !== nextKeys.length) return false;
  return prevKeys.every((key) => {
    const a = prev[key];
    const b = next[key];
    if (a === b) return true;
    if (Array.isArray(a) && Array.isArray(b)) {
      return (
        a.length === b.length &&
        a.every((item, index) => {
          const other = b[index];
          if (item === other) return true;
          return (
            typeof item === 'object' &&
            item !== null &&
            JSON.stringify(item) === JSON.stringify(other)
          );
        })
      );
    }
    return false;
  });
}

export default memo(MessageBubble, arePropsEqual);
